import { TOOL_METAS } from "./metaRegistry";

export type ToolCardIcon =
  | "chart"
  | "file-search"
  | "image-down"
  | "pipette"
  | "type"
  | "mic"
  | "scaling"
  | "file"
  | "minimize"
  | "palette"
  | "grid"
  | "library"
  | "link"
  | "layout"
  | "mouse-click"
  | "route"
  | "server"
  | "tags";

export type ToolPresentation = {
  id: string;
  title: string;
  desc: string;
  category: string;
  route: string;
  bg: string;
  fg: string;
  icon: ToolCardIcon;
  col?: number;
  row?: number;
};

type ToolVisualConfig = Pick<
  ToolPresentation,
  "bg" | "fg" | "icon" | "col" | "row"
>;

const DEFAULT_VISUALS: Record<string, ToolVisualConfig> = {
  Text: {
    bg: "#5dcbf0",
    fg: "#202c39",
    icon: "type",
    col: 1,
    row: 1,
  },
  Images: {
    bg: "#8ac926",
    fg: "#202c39",
    icon: "file",
    col: 1,
    row: 1,
  },
  URLs: {
    bg: "#1982c4",
    fg: "#ffffff",
    icon: "route",
    col: 1,
    row: 1,
  },
  SEO: {
    bg: "#1982c4",
    fg: "#ffffff",
    icon: "file-search",
    col: 1,
    row: 1,
  },
  Design: {
    bg: "#ff595e",
    fg: "#ffffff",
    icon: "palette",
    col: 1,
    row: 1,
  },
  Other: {
    bg: "#202c39",
    fg: "#ffffff",
    icon: "layout",
    col: 1,
    row: 1,
  },
};

const TOOL_VISUALS: Record<string, ToolVisualConfig> = {
  "campaign-url-architect": {
    bg: "#1982c4",
    fg: "#ffffff",
    icon: "tags",
  },
  "case-converter": {
    bg: "#5dcbf0",
    fg: "#202c39",
    icon: "type",
  },
  "color-palette-generator": {
    bg: "#ff595e",
    fg: "#ffffff",
    icon: "palette",
    col: 2,
  },
  "color-picker": {
    bg: "#1982c4",
    fg: "#ffffff",
    icon: "pipette",
    col: 2,
    row: 2,
  },
  dictate: {
    bg: "#ffca3a",
    fg: "#202c39",
    icon: "mic",
  },
  "dns-checker": {
    bg: "#1982c4",
    fg: "#ffffff",
    icon: "server",
  },
  "font-converter": {
    bg: "#ef476f",
    fg: "#ffffff",
    icon: "type",
  },
  "heatmap-tracker": {
    bg: "#ef476f",
    fg: "#ffffff",
    icon: "mouse-click",
  },
  "image-compressor": {
    bg: "#6a4c93",
    fg: "#ffffff",
    icon: "minimize",
  },
  "image-converter": {
    bg: "#577590",
    fg: "#ffffff",
    icon: "file",
  },
  "image-extractor": {
    bg: "#43aa8b",
    fg: "#ffffff",
    icon: "image-down",
  },
  "image-to-text": {
    bg: "#8ac926",
    fg: "#202c39",
    icon: "file",
    row: 2,
  },
  "lorem-ipsum-generator": {
    bg: "#f4a261",
    fg: "#202c39",
    icon: "type",
  },
  "modular-wireframe-generator": {
    bg: "#ff595e",
    fg: "#ffffff",
    icon: "layout",
  },
  "scroll-animator-studio": {
    bg: "#ffca3a",
    fg: "#202c39",
    icon: "chart",
    col: 2,
  },
  "proportion-scaler": {
    bg: "#202c39",
    fg: "#ffffff",
    icon: "scaling",
  },
  "redirect-analyzer": {
    bg: "#577590",
    fg: "#ffffff",
    icon: "route",
  },
  "sitemap-robots-explorer": {
    bg: "#1982c4",
    fg: "#ffffff",
    icon: "file-search",
  },
  vectorvault: {
    bg: "#ffca3a",
    fg: "#202c39",
    icon: "library",
    col: 2,
  },
};

const TOOL_ORDER = [
  "image-compressor",
  "image-to-text",
  "case-converter",
  "color-picker",
  "color-palette-generator",
  "proportion-scaler",
  "vectorvault",
  "dns-checker",
  "redirect-analyzer",
  "sitemap-robots-explorer",
  "modular-wireframe-generator",
  "scroll-animator-studio",
  "image-converter",
  "image-extractor",
  "font-converter",
  "heatmap-tracker",
  "dictate",
  "campaign-url-architect",
  "lorem-ipsum-generator",
];

export const TOOL_CATALOG: ToolPresentation[] = [...TOOL_METAS]
  .sort((a, b) => {
    const aIndex = TOOL_ORDER.indexOf(a.slug);
    const bIndex = TOOL_ORDER.indexOf(b.slug);
    const safeA = aIndex === -1 ? TOOL_ORDER.length : aIndex;
    const safeB = bIndex === -1 ? TOOL_ORDER.length : bIndex;

    return safeA - safeB || a.title.localeCompare(b.title);
  })
  .map((meta) => {
    const visuals = TOOL_VISUALS[meta.slug] ?? DEFAULT_VISUALS[meta.category];

    return {
      id: meta.slug,
      title: meta.title,
      desc: meta.description,
      category: meta.category,
      route: `/tools/${meta.slug}`,
      ...visuals,
    };
  });
